import React, { useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import useGetContentUsage, { ContentUsageType } from './useGetContentUsage';
import { PortalsContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import ScorecardContext from '../../contexts/ScorecardContext';
import Form from '../../kingpin/forms/Form';
import FormHeader from '../../kingpin/forms/FormHeader';
import Typography from '../../kingpin/atoms/Typography';
import { buildReportShow } from '../../navigation/appRoutes';
import DashboardContext from '../../contexts/DashboardContext';
import ReportContext from '../../contexts/ReportContext';
import toSentenceCase from '../../services/toSentenceCase';
import V3AppUsageItem from './V3AppUsageItem';
import EntityAppUsageItem from './EntityAppUsageItem';

const ContentUsage = ({ type }: { type: ContentUsageType }) => {
  const { selectedPortal } = useContext(PortalsContext);
  const { scorecard } = useContext(ScorecardContext);
  const { dashboard } = useContext(DashboardContext);
  const { report } = useContext(ReportContext);
  const getReportUsage = useGetContentUsage();

  const contentItem = useMemo(() => {
    if (dashboard && type === 'dashboard') {
      return { id: dashboard.id, name: dashboard.name, type };
    } else if (report && type === 'report') {
      return { id: report.id, name: report.name, type };
    } else if (scorecard && type === 'scorecard') {
      return { id: scorecard.id, name: scorecard.title, type };
    }
  }, [dashboard, report, scorecard, type]);

  if (!contentItem) {
    return null;
  }

  const usage = getReportUsage(contentItem.id, type);

  return (
    <Form>
      <FormHeader
        title={`${toSentenceCase(type)} Usage (${contentItem.name})`}
      />
      {usage.entityApps.length > 0 && (
        <>
          <div>
            <Typography.Header type={'H3'}>
              Entity Apps ({usage.entityApps.length})
            </Typography.Header>
          </div>
          {usage.entityApps.map((app) => (
            <EntityAppUsageItem
              app={app}
              contentItem={contentItem}
              key={app.id}
            />
          ))}
        </>
      )}
      {usage.v3Apps.length > 0 && (
        <>
          <div>
            <Typography.Header type={'H3'}>
              V3 Apps ({usage.v3Apps.length})
            </Typography.Header>
          </div>
          {usage.v3Apps.map((app) => (
            <V3AppUsageItem app={app} contentItem={contentItem} key={app.id} />
          ))}
        </>
      )}
      {usage.popupReports.length > 0 && (
        <>
          <div>
            <Typography.Header type={'H3'}>
              Popup reports ({usage.popupReports.length})
            </Typography.Header>
          </div>
          {usage.popupReports.map((report) => (
            <div key={report.id}>
              <Link
                target={'_blank'}
                to={buildReportShow({
                  id: report.id,
                  portal: selectedPortal,
                })}
              >
                {report.name}
              </Link>
            </div>
          ))}
        </>
      )}
    </Form>
  );
};

const Gate = ({
  isOpen,
  close,
  type,
}: {
  isOpen: boolean;
  close: () => void;
  type: ContentUsageType;
}) => {
  return (
    <ModalTransition>
      {isOpen && (
        <Modal onClose={close} autoFocus={false}>
          <ContentUsage type={type} />
        </Modal>
      )}
    </ModalTransition>
  );
};

export default Gate;
